import type { TemplateJson, TemplateNode } from '~/utils/templateSdk'
import { buildLocalizedPath, parseLocalePath, stripLocalePrefix } from '~/utils/localeController'

export type StoreTemplateMenu = {
  name?: unknown
  url?: unknown
}

export type StoreNavItem = {
  label: string
  href: string
  active: boolean
}

type StoreMenuLinkContext = {
  currentUrl?: string
  currentPath?: string
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const parseStoreTemplateJson = (value: unknown): unknown => {
  if (typeof value !== 'string') return value
  if (!value.trim()) return null
  try {
    return JSON.parse(value)
  } catch {
    try {
      return JSON.parse(decodeURIComponent(value))
    } catch {
      return null
    }
  }
}

const splitPathSuffix = (value: string) => {
  const [pathWithQuery = '', hash = ''] = value.split('#')
  const [pathname = '', query = ''] = pathWithQuery.split('?')
  return {
    pathname: pathname || '/',
    query: query ? `?${query}` : '',
    hash: hash ? `#${hash}` : '',
  }
}

const getPathFromUrl = (value: string): string => {
  const raw = value.trim()
  if (!raw) return '/'
  try {
    if (/^https?:\/\//i.test(raw)) {
      const parsed = new URL(raw)
      return `${parsed.pathname || '/'}${parsed.search}${parsed.hash}`
    }
  } catch {
    // fall back to raw path
  }
  return raw.startsWith('/') ? raw : `/${raw}`
}

const normalizePathForCompare = (value: string): string => {
  const rawPath = getPathFromUrl(value)
  const stripped = stripLocalePrefix(rawPath)
  return stripped.pathname.replace(/^\/+|\/+$/g, '') || ''
}

const resolveCurrentPath = (context: StoreMenuLinkContext): string => {
  if (context.currentPath?.trim()) return context.currentPath.trim()
  if (!context.currentUrl?.trim()) return '/'
  try {
    const parsed = new URL(context.currentUrl)
    return `${parsed.pathname || '/'}${parsed.search}${parsed.hash}`
  } catch {
    return getPathFromUrl(context.currentUrl)
  }
}

const isExternalUrl = (rawUrl: string, currentUrl?: string): boolean => {
  if (!/^https?:\/\//i.test(rawUrl)) return false
  if (!currentUrl) return false
  try {
    return new URL(rawUrl).origin !== new URL(currentUrl).origin
  } catch {
    return false
  }
}

export const resolveStoreMenuHref = (rawUrl: string, context: StoreMenuLinkContext = {}): string => {
  const value = rawUrl.trim()
  if (!value || /^(#|mailto:|tel:|javascript:)/i.test(value)) return value || '#'
  if (isExternalUrl(value, context.currentUrl)) return value

  const currentPath = resolveCurrentPath(context)
  const currentLocale = parseLocalePath(currentPath).locale
  const targetPath = getPathFromUrl(value)
  const target = splitPathSuffix(targetPath)
  const normalizedPathname = target.pathname.startsWith('/') ? target.pathname : `/${target.pathname}`

  if (!currentLocale) {
    const stripped = stripLocalePrefix(normalizedPathname)
    return `${stripped.pathname}${target.query}${target.hash}`
  }

  return buildLocalizedPath(`${normalizedPathname}${target.query}${target.hash}`, currentLocale)
}

export const pickStoreTemplateMenus = (value: unknown): unknown[] => {
  const parsed = parseStoreTemplateJson(value)
  if (!isRecord(parsed)) return []
  if (Array.isArray(parsed.menus)) return parsed.menus
  if (isRecord(parsed.data) && Array.isArray(parsed.data.menus)) return parsed.data.menus
  return []
}

export const normalizeStoreTemplateMenus = (
  menus: unknown,
  context: StoreMenuLinkContext = {},
): StoreNavItem[] => {
  if (!Array.isArray(menus)) return []
  const currentPath = normalizePathForCompare(resolveCurrentPath(context))

  return menus
    .map((item, index): StoreNavItem | null => {
      if (!isRecord(item)) return null
      const menu = item as StoreTemplateMenu
      const label = typeof menu.name === 'string' ? menu.name.trim() : ''
      const rawUrl = typeof menu.url === 'string' ? menu.url.trim() : ''
      if (!label || !rawUrl) return null

      const menuPath = normalizePathForCompare(rawUrl)
      const isActive = currentPath
        ? currentPath === menuPath || currentPath.startsWith(`${menuPath}/`)
        : index === 0
      return {
        label,
        href: resolveStoreMenuHref(rawUrl, context),
        active: isActive,
      }
    })
    .filter((item): item is StoreNavItem => !!item)
}

const applyMenusToNode = (node: TemplateNode, menus: StoreNavItem[]): TemplateNode => {
  const next: TemplateNode = {
    ...node,
    properties: node.component === 'SihongStoreNavBar'
      ? { ...(node.properties ?? {}), storeNavItems: menus }
      : node.properties,
  }
  if (node.body?.length) next.body = node.body.map((child) => applyMenusToNode(child, menus))
  return next
}

const applyMenusToRecordNode = (
  node: Record<string, unknown>,
  menus: StoreNavItem[],
): Record<string, unknown> => {
  const component = typeof node.component === 'string'
    ? node.component
    : typeof node.name === 'string'
      ? node.name
      : ''
  const next: Record<string, unknown> = { ...node }

  if (component === 'SihongStoreNavBar') {
    next.properties = {
      ...(isRecord(node.properties) ? node.properties : {}),
      storeNavItems: menus,
    }
  }

  if (Array.isArray(node.body)) {
    next.body = node.body.map((child) => isRecord(child) ? applyMenusToRecordNode(child, menus) : child)
  }

  return next
}

export const applyStoreMenusToTemplate = (template: TemplateJson, menus: StoreNavItem[]): TemplateJson => {
  if (!menus.length) return template

  if (Array.isArray(template)) return template.map((node) => applyMenusToNode(node, menus))

  if (isRecord(template)) {
    if (typeof template.component === 'string') return applyMenusToNode(template as TemplateNode, menus)
    if (Array.isArray(template.components)) {
      return {
        ...template,
        components: template.components.map((node) => isRecord(node) ? applyMenusToRecordNode(node, menus) : node),
      } as TemplateJson
    }

    return Object.fromEntries(
      Object.entries(template).map(([key, value]) => [
        key,
        isRecord(value) ? applyMenusToRecordNode(value, menus) : value,
      ]),
    ) as TemplateJson
  }

  return template
}